import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  MdFilterList,
  MdClose,
  MdSearch,
  MdArrowDropDown,
  MdClear,
  MdKeyboardArrowDown,
} from "react-icons/md";
import FilterSelect from "../Atoms/FilterSelect/FilterSelect";
import DateRangePickerModal from "./DateRangePickerModal";

/**
 * FilterBar
 *
 * FilterField shape:
 *   {
 *     key:         string,
 *     type:        "select" | "text" | "date" | "daterange" | "search" | "asyncDropdown",
 *     label:       string,
 *     placeholder? string,
 *     options?:    { value: string, label: string }[],   — for "select"
 *     load?:       (search: string) => Promise<{value,label}[]>,  — for "asyncDropdown"
 *     width?:      string,
 *   }
 */

/* ─────────────── AsyncDropdown ─────────────── */

/* ─────────────── AsyncDropdown ─────────────── */
const AsyncDropdownFilter = ({ field, value, onChange }) => {
  const id = useId();
  const [selectedLabel, setSelectedLabel] = useState("");

  const loadOptions = useCallback(
    (search) => (field.load ? field.load(search) : Promise.resolve([])),
    [field.load],
  );

  // Resolve the selected ID to its corresponding store name.
  useEffect(() => {
    let cancelled = false;

    if (!value) {
      setSelectedLabel("");
      return;
    }

    // Avoid showing the previous seller's name while resolving a new ID.
    setSelectedLabel("");

    const resolveLabel = async () => {
      try {
        const options = await loadOptions("");

        if (cancelled) return;

        const matchedOption = (options || []).find(
          (option) => String(option.value) === String(value),
        );

        if (matchedOption) {
          setSelectedLabel(matchedOption.label);
        }
      } catch (error) {
        console.error("Failed to resolve selected dropdown label:", error);
      }
    };

    resolveLabel();

    return () => {
      cancelled = true;
    };
  }, [value, loadOptions]);

  const selectedOption = value
    ? {
        value,
        label: selectedLabel || String(value),
      }
    : null;

  return (
    <div className="flex min-w-0 w-full flex-col gap-1">
      {field.label && (
        <label
          htmlFor={id}
          className="px-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-700"
        >
          {field.label}
        </label>
      )}

      <FilterSelect
        inputId={id}
        value={selectedOption}
        onChange={(option) => {
          setSelectedLabel(option?.label || "");
          onChange(field.key, option?.value || "");
        }}
        loadOptions={loadOptions}
        placeholder={field.placeholder || `All ${field.label || ""}`}
        isClearable={Boolean(value)}
        isDisabled={field.disabled}
        isSearchable
        className="!mb-0"
      />
    </div>
  );
};

const RANGE_PAIRS = [
  ["fromDate", "toDate"],
  ["startDate", "endDate"],
  ["dateFrom", "dateTo"],
];
const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const EMPTY_FILTER_VALUES = Object.freeze({});

const unwrapFilterValue = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    if ("value" in value) {
      return value.value;
    }
  }

  return value;
};

export const normalizeFilterValues = (values = {}) => {
  if (!values || typeof values !== "object") {
    return {};
  }

  return Object.fromEntries(
    Object.entries(values).filter(([, value]) => {
      const normalized = unwrapFilterValue(value);
      return (
        normalized !== undefined &&
        normalized !== null &&
        normalized !== "" &&
        String(normalized).toLowerCase() !== "all"
      );
    }),
  );
};

export const resolveSelectedOption = (field, value) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  const normalizedValue = unwrapFilterValue(value);

  if (
    normalizedValue === undefined ||
    normalizedValue === null ||
    normalizedValue === ""
  ) {
    return null;
  }

  return (
    (field.options || []).find(
      (opt) => String(opt.value) === String(normalizedValue),
    ) || null
  );
};

const normalizeKey = (key = "") => String(key).trim().toLowerCase();

const toInputDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getLocalToday = () => toInputDate(new Date());

const parseInputDate = (value) => {
  if (!value) return null;
  const [year, month, day] = String(value).split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const addMonths = (date, amount) => {
  const next = new Date(date);
  next.setMonth(next.getMonth() + amount, 1);
  return next;
};

const buildCalendarDays = (viewDate) => {
  const monthStart = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
  const gridStart = new Date(monthStart);
  gridStart.setDate(monthStart.getDate() - monthStart.getDay());

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + index);
    return {
      date,
      value: toInputDate(date),
      day: date.getDate(),
      isCurrentMonth: date.getMonth() === viewDate.getMonth(),
    };
  });
};

const isBetweenDates = (value, start, end) =>
  Boolean(start && end && value >= start && value <= end);

const formatDateLabel = (value) => {
  if (!value) return "";
  const date = parseInputDate(value);
  if (!date || Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const isDateRangeStart = (field, nextField) => {
  if (
    !field ||
    !nextField ||
    field.type !== "date" ||
    nextField.type !== "date"
  ) {
    return null;
  }

  return (
    RANGE_PAIRS.find(
      ([startKey, endKey]) =>
        normalizeKey(field.key) === normalizeKey(startKey) &&
        normalizeKey(nextField.key) === normalizeKey(endKey),
    ) || null
  );
};

const resolveDateRangeLabel = (startField = {}, endField = {}) => {
  if (startField.rangeLabel) return startField.rangeLabel;
  if (endField.rangeLabel) return endField.rangeLabel;

  const labels = [startField.label, endField.label]
    .filter(Boolean)
    .map((label) => String(label).trim().toLowerCase());

  if (
    labels.length === 2 &&
    labels.some((label) => ["from", "from date"].includes(label)) &&
    labels.some((label) => ["to", "to date"].includes(label))
  ) {
    return "Date Range";
  }

  return startField.label && endField.label
    ? `${startField.label} - ${endField.label}`
    : "Date Range";
};

export const GoldDateRangeCalendar = ({
  dates,
  viewDate,
  onViewDateChange,
  onSelectDate,
  onApply,
  onCancel,
  onClear,
  onToday,
  loading,
  maxDate,
  disableFuture = true,
}) => {
  const days = useMemo(() => buildCalendarDays(viewDate), [viewDate]);
  const hasCompleteRange = Boolean(dates.fromDate && dates.toDate);

  const today = new Date();
  const todayValue = toInputDate(today);

  return (
    <div className="w-full">
      <div className="mb-5 flex w-full flex-nowrap justify-between gap-2">
        <button
          type="button"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-lg font-medium text-slate-700 shadow-xs transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 disabled:opacity-40"
          onClick={() => onViewDateChange(addMonths(viewDate, -1))}
          disabled={loading}
          aria-label="Previous month"
        >
          ‹
        </button>

        <div className="flex flex-col items-center gap-1.5">
          <div className="flex min-w-0 flex-nowrap items-center justify-center gap-1.5">
            {/* Month dropdown */}
            {(() => {
              const monthLabels = [
                "January",
                "February",
                "March",
                "April",
                "May",
                "June",
                "July",
                "August",
                "September",
                "October",
                "November",
                "December",
              ];

              const monthOptions = monthLabels.map((m, i) => ({
                value: i,
                label: m,
              }));

              return (
                <FilterSelect
                  className="shrink-0"
                  options={monthOptions}
                  value={
                    monthOptions.find(
                      (o) => Number(o.value) === Number(viewDate.getMonth()),
                    ) || null
                  }
                  onChange={(selected) => {
                    if (!selected) return;
                    const nextDate = new Date(viewDate);
                    nextDate.setDate(1);
                    nextDate.setMonth(Number(selected.value));
                    onViewDateChange(nextDate);
                  }}
                  isDisabled={loading}
                  isSearchable={false}
                  placeholder="Month"
                />
              );
            })()}

            {/* Year dropdown */}
            {(() => {
              const startYear = new Date().getFullYear() - 10;
              const yearOptions = Array.from({ length: 21 }, (_, index) => {
                const year = startYear + index;
                return { value: year, label: String(year) };
              });

              return (
                <FilterSelect
                  className="shrink-0"
                  options={yearOptions}
                  value={
                    yearOptions.find(
                      (o) => Number(o.value) === Number(viewDate.getFullYear()),
                    ) || null
                  }
                  onChange={(selected) => {
                    if (!selected) return;
                    const nextDate = new Date(viewDate);
                    nextDate.setDate(1);
                    nextDate.setFullYear(Number(selected.value));
                    onViewDateChange(nextDate);
                  }}
                  isDisabled={loading}
                  isSearchable={false}
                  placeholder="Year"
                />
              );
            })()}
          </div>

          {!dates.fromDate || !dates.toDate ? (
            <p className="text-xs font-medium text-slate-500">
              {!dates.fromDate ? "Select start date" : "Select end date"}
            </p>
          ) : null}
        </div>

        <button
          type="button"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-lg font-medium text-slate-700 shadow-xs transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 disabled:opacity-40"
          onClick={() => onViewDateChange(addMonths(viewDate, 1))}
          disabled={loading}
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      {/* Selected Range Display Box - shown on top after selection */}
      <div className="mb-3 w-full">
        {!dates.fromDate ? (
          <span className="text-sm text-slate-400"></span>
        ) : !dates.toDate ? (
          <div className="flex items-center justify-end gap-3">
            <div className="rounded-md bg-[var(--admin-gold-soft)]/40 px-3 py-1 text-[11px] font-semibold text-[var(--admin-gold)]">
              {formatDateLabel(dates.fromDate)}
            </div>
            <span className="text-[11px] text-[var(--admin-muted)]">
              Select end date
            </span>
          </div>
        ) : (
          <div className="flex justify-end">
            <div className="inline-flex items-center gap-3 rounded-lg bg-white/90 px-3 py-2 text-xs font-medium">
              <span className="text-[var(--admin-ink)]">
                {formatDateLabel(dates.fromDate)}
              </span>
              <span className="text-slate-400">→</span>
              <span className="text-[var(--admin-ink)]">
                {formatDateLabel(dates.toDate)}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="mb-2 grid grid-cols-7 gap-1 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">
        {WEEKDAY_LABELS.map((label) => (
          <span key={label} className="py-1">
            {label}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {days.map((day) => {
          const isFutureDate = day.value > todayValue;
          const isStart = day.value === dates.fromDate;
          const isEnd = day.value === dates.toDate;
          const isSelected = isStart || isEnd;
          const isToday = day.value === todayValue;

          const isInRange = isBetweenDates(
            day.value,
            dates.fromDate,
            dates.toDate,
          );

          const isDisabled =
            (disableFuture && isFutureDate) ||
            Boolean(maxDate && day.value > maxDate);

          return (
            <button
              key={day.value}
              type="button"
              className={`mx-auto flex h-9 w-9 items-center justify-center rounded-lg text-xs font-medium transition-all ${
                isSelected
                  ? "bg-[var(--admin-navy)] text-white font-semibold shadow-xs"
                  : isInRange
                    ? "bg-[var(--admin-gold-soft)] text-[var(--admin-navy)] font-semibold"
                    : isDisabled
                      ? "text-slate-300 cursor-not-allowed bg-transparent"
                      : isToday
                        ? "border border-[var(--admin-gold)] font-bold text-[var(--admin-gold-dark)] bg-[var(--admin-gold-soft)]/90 hover:bg-[var(--admin-gold-soft)]/100"
                        : day.isCurrentMonth
                          ? "text-[var(--admin-ink)] hover:bg-white hover:text-[var(--admin-navy-dark)]"
                          : "text-slate-400 hover:bg-slate-50 hover:text-slate-600"
              } disabled:cursor-not-allowed disabled:bg-transparent disabled:shadow-none`}
              onClick={() => {
                if (isDisabled) return;
                onSelectDate(day.value);
              }}
              disabled={loading || isDisabled}
            >
              {day.day}
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="inline-flex h-9 items-center justify-center rounded-lg border border-slate-200 bg-white px-3.5 text-xs font-medium text-slate-700 shadow-2xs transition hover:bg-slate-50 hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={onToday}
            disabled={loading}
          >
            Today
          </button>

          <button
            type="button"
            className="inline-flex h-9 items-center justify-center rounded-lg bg-red-100 px-3 text-xs font-medium text-red-600 transition disabled:cursor-not-allowed disabled:opacity-40"
            onClick={onClear}
            disabled={loading || (!dates.fromDate && !dates.toDate)}
          >
            Clear
          </button>
        </div>

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            className="inline-flex h-9 items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-xs font-medium text-slate-700 shadow-2xs transition hover:bg-slate-50 hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>

          <button
            type="button"
            className="inline-flex h-9 min-w-[90px] items-center justify-center rounded-lg bg-[var(--admin-gold)] text-white px-4 text-xs font-semibold shadow-sm transition hover:bg-[var(--admin-gold-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--admin-gold)]/40 disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-slate-200 disabled:text-slate-400"
            disabled={!hasCompleteRange || loading}
            onClick={onApply}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export const DateRangeFilter = ({
  field,
  value,
  onChange,
  values,
  onApply: onApplyOverride,
  loading = false,
}) => {
  const id = useId();
  const startKey = field.startKey || "startDate";
  const endKey = field.endKey || "endDate";
  const startValue = values?.[startKey] ?? value?.startDate ?? "";
  const endValue = values?.[endKey] ?? value?.endDate ?? "";
  const today = getLocalToday();
  const maxDate = field.disableFuture ? today : field.maxDate;
  const [open, setOpen] = useState(false);
  const [draftDates, setDraftDates] = useState({
    fromDate: startValue,
    toDate: endValue,
  });
  const [viewDate, setViewDate] = useState(
    () => parseInputDate(startValue) || new Date(),
  );

  useEffect(() => {
    if (open) return;
    setDraftDates({ fromDate: startValue, toDate: endValue });
  }, [endValue, open, startValue]);

  const displayLabel =
    startValue && endValue
      ? `${formatDateLabel(startValue)} - ${formatDateLabel(endValue)}`
      : field.placeholder || `All ${field.label || "Date Range"}`;

  const applyRange = () => {
    if (!draftDates.fromDate || !draftDates.toDate) return;
    const nextDates =
      draftDates.fromDate <= draftDates.toDate
        ? draftDates
        : { fromDate: draftDates.toDate, toDate: draftDates.fromDate };
    if (onApplyOverride) {
      // Range consumers can update both dates in one state transition. Calling
      // onChange twice here can make the second update restore a stale start.
      onApplyOverride(nextDates.fromDate, nextDates.toDate);
    } else {
      onChange(startKey, nextDates.fromDate);
      onChange(endKey, nextDates.toDate);
    }
    setOpen(false);
  };

  const clearRange = () => {
    setDraftDates({
      fromDate: "",
      toDate: "",
    });
  };

  const selectToday = () => {
    const todayValue = toInputDate(new Date());
    setDraftDates({ fromDate: todayValue, toDate: todayValue });
    setViewDate(new Date());
  };

  const selectDate = (valueToSelect) => {
    const selectedDate = parseInputDate(valueToSelect);
    if (!selectedDate) return;
    setViewDate(selectedDate);
    setDraftDates((current) => {
      // A new selection always begins at the clicked date. This lets users
      // replace both ends of an already-applied range, including selecting
      // dates earlier than the old start date.
      if (!current.fromDate || !current.toDate) {
        if (!current.fromDate) {
          return { fromDate: valueToSelect, toDate: "" };
        }
        if (valueToSelect < current.fromDate) {
          return { fromDate: valueToSelect, toDate: current.fromDate };
        }
        return { ...current, toDate: valueToSelect };
      }

      if (current.fromDate && current.toDate) {
        return { fromDate: valueToSelect, toDate: "" };
      }
      return current;
    });
  };

  const openPicker = () => {
    setDraftDates({ fromDate: startValue, toDate: endValue });
    setViewDate(parseInputDate(startValue || endValue) || new Date());
    setOpen(true);
  };

  return (
    <div
      className={`flex min-w-[225px] flex-col ${
        field.labelClassName ? "gap-0" : "gap-1"
      } ${field.wrapperClassName || ""} ${field.width || "w-full"}`}
    >
      {field.label && (
        <label
          htmlFor={id}
          className={
            field.labelClassName ||
            "px-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-700"
          }
        >
          {field.label}
          {field.required && <span className="ml-0.5 text-red-500">*</span>}
        </label>
      )}

      <button
        id={id}
        type="button"
        onClick={() => {
          if (open) {
            setOpen(false);
          } else {
            openPicker();
          }
        }}
        className={`admin-input !flex !h-9 w-full items-center justify-between gap-2 !py-0 text-left !text-[13px] !leading-none ${
          field.inputClassName || ""
        } ${
          startValue && endValue ? "text-[var(--admin-ink)]" : "text-gray-500"
        }`}
      >
        <span className="whitespace-nowrap">{displayLabel}</span>

        <MdKeyboardArrowDown
          aria-hidden="true"
          size={22}
          color="hsl(0, 0%, 80%)"
          className={`shrink-0 transition-transform duration-200 ${
            open ? "rotate-180" : "rotate-0"
          }`}
        />
      </button>

      <DateRangePickerModal
        open={open}
        onClose={() => setOpen(false)}
        title="Select Date Range"
        subtitle={field.subtitle || "Filter data will update after apply."}
        loading={loading}
      >
        <GoldDateRangeCalendar
          dates={draftDates}
          viewDate={viewDate}
          onViewDateChange={setViewDate}
          onSelectDate={selectDate}
          onApply={applyRange}
          onCancel={() => setOpen(false)}
          onClear={clearRange}
          onToday={selectToday}
          maxDate={maxDate}
          disableFuture={field.disableFuture ?? true}
          loading={loading}
        />
      </DateRangePickerModal>
    </div>
  );
};

/* ─────────────── Debounced Text Filter ─────────────── */

const DebouncedTextFilter = ({ field, value, onChange, id, wrapperClass }) => {
  const [inputValue, setInputValue] = useState(value ?? "");

  const debounceRef = useRef(null);

  /*
   * Keep the input in sync when the value is changed externally.
   *
   * Example:
   * - Reset filters
   * - Clear button
   * - Pagination reset
   */
  useEffect(() => {
    setInputValue(value ?? "");
  }, [value]);

  /*
   * Cleanup timer when component unmounts.
   */
  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current);
    };
  }, []);

  const handleChange = useCallback(
    (e) => {
      const nextValue = e.target.value;

      /*
       * IMPORTANT:
       * Update local state immediately.
       *
       * This allows continuous typing without
       * waiting for debounce.
       */
      setInputValue(nextValue);

      /*
       * Cancel previous debounce.
       */
      clearTimeout(debounceRef.current);

      /*
       * Wait 400ms after the user stops typing.
       */
      debounceRef.current = setTimeout(() => {
        onChange(field.key, nextValue);
      }, 400);
    },
    [field.key, onChange],
  );

  const handleClear = useCallback(() => {
    clearTimeout(debounceRef.current);

    setInputValue("");

    onChange(field.key, "");
  }, [field.key, onChange]);

  return (
    <div className={wrapperClass}>
      {field.label && (
        <label
          htmlFor={id}
          className="px-0.5  text-[10px] font-medium uppercase tracking-wide text-gray-700"
        >
          {field.label}
        </label>
      )}

      <div className="relative ">
        {field.type === "search" && (
          <MdSearch
            size={14}
            className="pointer-events-none  absolute left-3 top-1/2 -translate-y-1/2 text-[var(--admin-muted)]"
          />
        )}

        <input
          id={id}
          type="text"
          value={inputValue}
          onChange={handleChange}
          placeholder={field.placeholder || `Search ${field.label || ""}…`}
          className={`admin-input min-h-9 w-full text-sm ${
            field.type === "search" ? "!pl-9" : ""
          } ${inputValue && field.showClear !== false ? "!pr-9" : ""}`}
        />

        {inputValue && field.showClear !== false && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-gray-300 transition-colors hover:text-gray-500 focus:outline-none"
            aria-label={`Clear ${field.label || "search"}`}
          >
            <MdClear size={16} />
          </button>
        )}
      </div>
    </div>
  );
};

/* ─────────────── FilterField ─────────────── */
const FilterField = ({ field, value, onChange, values }) => {
  const id = useId();
  const wrapperClass = "flex min-w-0 flex-col gap-1 w-full";

  /* ─────────────── Async Dropdown ─────────────── */
  if (field.type === "asyncDropdown") {
    return (
      <AsyncDropdownFilter field={field} value={value} onChange={onChange} />
    );
  }

  /* ─────────────── Select ─────────────── */
  if (field.type === "select") {
    const selectedOption = resolveSelectedOption(field, value);
    const hasActiveValue = selectedOption !== null;

    return (
      <div className={wrapperClass}>
        {field.label && (
          <label
            htmlFor={id}
            className="px-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-700"
          >
            {field.label}
          </label>
        )}

        <FilterSelect
          options={field.options || []}
          isLoading={field.isLoading}
          value={selectedOption}
          onChange={(opt) => onChange(field.key, opt ? opt.value : "")}
          placeholder={field.placeholder || `All ${field.label || ""}`}
          isSearchable={field.isSearchable ?? true}
          isClearable={field.isClearable ?? hasActiveValue}
          isDisabled={field.disabled}
          inputId={id}
          className="!mb-0"
        />
      </div>
    );
  }

  /* ─────────────── Text / Search ─────────────── */
  if (field.type === "text" || field.type === "search") {
    return (
      <DebouncedTextFilter
        field={field}
        value={value}
        onChange={onChange}
        id={id}
        wrapperClass={wrapperClass}
      />
    );
  }

  /* ─────────────── Date ─────────────── */
  if (field.type === "date") {
    const today = getLocalToday();

    const maxDate = field.maxDate ?? today;

    const isEndDate = field.key === "endDate" || field.key === "toDate";

    const minDate =
      isEndDate && values?.fromDate ? values.fromDate : field.minDate;

    return (
      <div className={wrapperClass}>
        {field.label && (
          <label
            htmlFor={id}
            className="px-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-700"
          >
            {field.label}
          </label>
        )}

        <input
          id={id}
          type="date"
          value={value ?? ""}
          onChange={(e) => onChange(field.key, e.target.value)}
          min={minDate}
          max={maxDate}
          className="admin-input min-h-9 w-full text-sm"
        />
      </div>
    );
  }

  /* ─────────────── Date Range ─────────────── */
  if (field.type === "daterange") {
    return (
      <DateRangeFilter
        field={field}
        value={value}
        onChange={onChange}
        values={values}
        loading={field.loading}
        onApply={field.onApply}
      />
    );
  }

  return null;
};

/* ─────────────── FilterBar ─────────────── */
const FilterBar = ({
  filters = [],
  fields,
  values = EMPTY_FILTER_VALUES,
  onChange,
  onClear,
  loading = false,
  activeCount = 0,
  listPage,
  gridClassName = "",
}) => {
  const resolvedFilters = useMemo(
    () => (filters.length ? filters : fields || []),
    [fields, filters],
  );

  const resolvedValues = useMemo(
    () => normalizeFilterValues(listPage?.filters || values),
    [listPage?.filters, values],
  );

  const resolvedOnClear = useCallback(() => {
    if (onClear) {
      onClear();
    } else {
      listPage?.clearFilters?.();
    }

    listPage?.clearSearch?.();
  }, [listPage, onClear]);

  // IMPORTANT:
  // normalizedFilters MUST come before resolvedOnChange
  const normalizedFilters = useMemo(() => {
    const nextFilters = [];

    for (let index = 0; index < resolvedFilters.length; index += 1) {
      const field = resolvedFilters[index];
      const nextField = resolvedFilters[index + 1];

      const rangePair = isDateRangeStart(field, nextField);

      if (rangePair) {
        const [startKey, endKey] = rangePair;

        nextFilters.push({
          ...field,
          key: `${startKey}__${endKey}`,
          type: "daterange",
          label: resolveDateRangeLabel(field, nextField),
          startKey: field.key || startKey,
          endKey: nextField.key || endKey,
          minDate: field.minDate,
          maxDate: nextField.maxDate ?? field.maxDate,
          disableFuture: field.disableFuture ?? nextField.disableFuture ?? true,
          width:
            field.rangeWidth ||
            nextField.rangeWidth ||
            field.width ||
            nextField.width ||
            "w-full",
        });

        index += 1;
        continue;
      }

      if (field.type !== "date") {
        nextFilters.push(field);
        continue;
      }

      const lowerKey = String(field.key || "").toLowerCase();

      if (lowerKey === "from" || lowerKey === "startdate") {
        nextFilters.push({
          ...field,
          key: "startDate",
        });
        continue;
      }

      if (lowerKey === "to" || lowerKey === "enddate") {
        nextFilters.push({
          ...field,
          key: "endDate",
        });
        continue;
      }

      nextFilters.push(field);
    }

    return nextFilters;
  }, [resolvedFilters]);

  // NOW normalizedFilters exists, so this is safe
  const resolvedOnChange = useCallback(
    (key, value) => {
      if (listPage?.setFilter) {
        listPage.setFilter(key, value);
        return;
      }

      onChange?.(key, value);
    },
    [listPage, onChange],
  );

  const resolvedActiveCount = useMemo(() => {
    return normalizedFilters.reduce((count, field) => {
      if (field.type === "daterange") {
        const startValue = resolvedValues[field.startKey];
        const endValue = resolvedValues[field.endKey];

        if (startValue || endValue) {
          return count + 1;
        }

        return count;
      }

      const value = resolvedValues[field.key];

      if (
        value !== "" &&
        value !== null &&
        value !== undefined &&
        (!Array.isArray(value) || value.length > 0)
      ) {
        return count + 1;
      }

      return count;
    }, 0);
  }, [normalizedFilters, resolvedValues]);

  if (!normalizedFilters.length) {
    return null;
  }

  return (
    <div className="relative border-b border-[var(--admin-line)] bg-[#FFFDF8] px-3 py-3 sm:px-4">
      <div className="mb-3 flex min-h-8 items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--admin-muted)]">
          <MdFilterList aria-hidden="true" size={16} />
          Filters
          <span
            className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--admin-gold)] px-1.5 text-[10px] font-bold text-[var(--admin-navy)] transition-opacity ${
              resolvedActiveCount > 0 ? "opacity-100" : "opacity-0"
            }`}
            aria-hidden={resolvedActiveCount === 0}
          >
            {resolvedActiveCount || 0}
          </span>
        </div>

        {resolvedOnClear && (
          <button
            type="button"
            onClick={resolvedOnClear}
            disabled={loading || resolvedActiveCount === 0}
            tabIndex={resolvedActiveCount > 0 ? 0 : -1}
            className={`inline-flex h-8 items-center gap-1 rounded-md border border-red-100 bg-white px-2.5 text-xs font-semibold text-red-500 transition-opacity hover:bg-red-50 hover:text-red-700 disabled:pointer-events-none ${
              resolvedActiveCount > 0 ? "opacity-100" : "opacity-0"
            }`}
            aria-hidden={resolvedActiveCount === 0}
          >
            <MdClose aria-hidden="true" size={13} />
            Reset
          </button>
        )}
      </div>

      <div
        className={`grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 ${gridClassName}`}
      >
        {normalizedFilters.map((field) => (
          <FilterField
            key={field.key}
            field={field}
            value={resolvedValues[field.key]}
            values={resolvedValues}
            onChange={resolvedOnChange}
          />
        ))}
      </div>
    </div>
  );
};

export default FilterBar;
